/**
 * Endpoint client for the `/datte/mf` resources (spec §3.5).
 *
 * `callEndpoint` builds the URL, fetches with `redirect: 'follow'` (so a stale
 * hash → 302 → fresh URL is transparently followed), checks `response.ok`,
 * `zod.parse`s the body, then re-attaches `response.url` as `finalUrl` — the
 * post-302 URL from which the caller adopts the fresh version hash
 * (`versionHashFromUrl`). The client NEVER builds the hash itself; it reads it
 * from the page attribute and adopts whatever the redirect resolves to.
 *
 * @see docs/speed-optimization/research/2026-06-06 - datte-model-finder-endpoints/spec.md §3.3/§3.5
 */

import type { ZodType, ZodTypeDef } from 'zod';

import {
  type NodeId,
  type SubtreeHash,
  type LevelsHash,
  subtreeHash,
} from '../domain/brand';
import {
  SubtreeResponse,
  LevelsResponse,
  type SubtreeResult,
  type LevelsResponse as LevelsResponseType,
} from './wire';

/** Thrown when a `/datte` response is not `ok` (after redirects are followed). */
export class EndpointError extends Error {
  constructor(
    readonly status: number,
    readonly url: string
  ) {
    super(`Endpoint ${url} failed with HTTP ${status}`);
    this.name = 'EndpointError';
  }
}

/**
 * A typed endpoint definition: URL builder + the zod schema for its body.
 *
 * The schema's INPUT type is `unknown` (not `T`): since branding ids at the wire
 * (ADR-0011) makes a schema a `ZodEffects` whose parsed OUTPUT (`NodeId`) differs
 * from its raw input (`number`), pinning input to `T` would reject it. We only
 * ever feed `.parse()` an `unknown` JSON body, so the input type is irrelevant —
 * widening it keeps `defineEndpoint` generic over transform-bearing schemas.
 */
export interface EndpointDef<P, T> {
  url(params: P): string;
  schema: ZodType<T, ZodTypeDef, unknown>;
}

/** Define an endpoint (URL builder + schema). */
export function defineEndpoint<P, T>(
  url: (params: P) => string,
  schema: ZodType<T, ZodTypeDef, unknown>
): EndpointDef<P, T> {
  return { url, schema };
}

/**
 * Fetch + parse an endpoint. Follows redirects, validates the body, and returns
 * the parsed value with the FINAL response URL attached as `finalUrl` (zod
 * strips unknown keys, so it is added after `.parse()`).
 */
export async function callEndpoint<P, T>(
  def: EndpointDef<P, T>,
  params: P
): Promise<T & { finalUrl: string }> {
  const url = def.url(params);
  const response = await fetch(url, { redirect: 'follow' });
  if (!response.ok) throw new EndpointError(response.status, url);
  const parsed = def.schema.parse(await response.json());
  // Attach the FINAL (post-redirect) URL without losing array-ness: an array
  // body (e.g. /levels) stays an array carrying an extra `finalUrl` property; an
  // object body gets the property added in place. (A spread would turn an array
  // into a plain object.)
  return Object.assign(parsed as object, { finalUrl: response.url }) as T & { finalUrl: string };
}

/**
 * The version hash is the path segment right after the resource name
 * (`subtree`/`levels`). It contains a `:` (e.g. `v11:a3f9`) and carries no
 * language (D24); take the whole segment.
 */
export function versionHashFromUrl(finalUrl: string): SubtreeHash | null {
  // `finalUrl` is `Response.url`, which is absolute; the `location.origin` base
  // only matters for a relative input and is read lazily so this works in a
  // non-browser (test) context too.
  const base = typeof location !== 'undefined' ? location.origin : 'http://localhost';
  const segs = new URL(finalUrl, base).pathname.split('/').filter(Boolean);
  const i = segs.findIndex((s) => s === 'subtree' || s === 'levels');
  // It only ever re-derives the SUBTREE hash (D24): the levels URL never feeds a
  // 302-hash-adoption loop, so a single brand at this boundary is correct.
  return i >= 0 && segs[i + 1] ? subtreeHash(decodeURIComponent(segs[i + 1])) : null;
}

/** Params for the subtree URL: version hash + node id + child depth. */
export interface SubtreeParams {
  hash: SubtreeHash;
  id: NodeId;
  depth: number;
}

/** Params for the levels URL: version hash + language ISO. */
export interface LevelsParams {
  hash: LevelsHash;
  lang: string;
}

/**
 * `GET /datte/mf/subtree/<hash>/<id>?depth=N` (language-neutral).
 *
 * `id` is interpolated raw, deliberately NOT `encodeURIComponent`'d: both shapes
 * are URL-safe by construction — a (possibly negative) integer, or a 22-char
 * base64url public id (`[A-Za-z0-9_-]`, W0-4 spec §3.4) — and every id has passed
 * the wire schema or `parseNodeId` first. Encoding would be a no-op today and a
 * silent double-encode if a caller ever pre-encoded.
 */
export const mfSubtree = defineEndpoint<SubtreeParams, SubtreeResponse>(
  ({ hash, id, depth }) =>
    `/datte/mf/subtree/${encodeURIComponent(hash)}/${id}?depth=${depth}`,
  SubtreeResponse
);

/** `GET /datte/mf/levels/<hash>/<lang>`. */
export const mfLevels = defineEndpoint<LevelsParams, LevelsResponseType>(
  ({ hash, lang }) =>
    `/datte/mf/levels/${encodeURIComponent(hash)}/${encodeURIComponent(lang)}`,
  LevelsResponse
);

/**
 * Fetch the backend-owned level labels once, degrading to `[]` on any error
 * (the finder then renders columns with no titles rather than failing to boot).
 * The returned array carries an extra `finalUrl` property the consumers ignore.
 */
export async function fetchLevels(hash: LevelsHash, lang: string): Promise<LevelsResponseType> {
  try {
    return await callEndpoint(mfLevels, { hash, lang });
  } catch {
    return [];
  }
}

// Re-export for callers that want the result type with finalUrl attached.
export type { SubtreeResult };
